import { useState } from 'react';

// Hardcoded plans — amounts match the Stripe test prices
// Per-month values: quarterly = 12.99/3 = 4.33, yearly = 39.99/12 = 3.33
const PLANS = [
  {
    key: 'monthly',
    label: 'Monthly',
    price: 'A$4.99',
    period: '/ month',
    perMonth: null,
    badge: null,
    badgeKind: null,
  },
  {
    key: 'quarterly',
    label: '3 Months',
    price: 'A$12.99',
    period: '/ 3 months',
    perMonth: 'A$4.33/mo',
    badge: 'Most Popular',
    badgeKind: 'primary',
  },
  {
    key: 'yearly',
    label: 'Yearly',
    price: 'A$39.99',
    period: '/ year',
    perMonth: 'A$3.33/mo',
    badge: 'Best Value',
    badgeKind: 'subtle',
  },
];

const FEATURES = [
  { icon: '♾️', text: 'Unlimited AI listing generations' },
  { icon: '⚡', text: '1-tap copy to Facebook Marketplace' },
  { icon: '📦', text: 'Single, bundle & bulk listing modes' },
  { icon: '🎨', text: 'Custom tone, style & condition settings' },
  { icon: '📍', text: 'Auto-include pickup location & price' },
];

export default function LandingPage({ onGetStarted, onSignIn }) {
  const [selectedPlan, setSelectedPlan] = useState('quarterly');

  return (
    <div className="lp-shell">
      <div className="lp-safe-top" />

      {/* ── Header */}
      <header className="lp-header">
        <div className="lp-header-inner">
          <div className="lp-logo">
            <img src="/media/logo-mascot.png" className="lp-logo-mark" alt="" aria-hidden="true" />
            <span className="lp-logo-text">sold4you</span>
          </div>
          <button className="lp-signin-btn" onClick={onSignIn}>Sign in</button>
        </div>
      </header>

      {/* ── Hero */}
      <section className="lp-hero">
        <div className="lp-mascot-wrap" aria-hidden="true">
          <img src="/media/logo-mascot.png" className="lp-mascot" alt="sold4you character" />
        </div>
        <h1 className="lp-headline">
          Snap a photo.<br />Get a ready-to-post listing.
        </h1>
        <p className="lp-sub lp-sub--lead">
          sold4you uses AI to turn your item photos into polished marketplace listings in seconds.
        </p>
        <p className="lp-sub lp-sub--accent">
          Helping you turn clutter into cash in no time!
        </p>
        <div className="lp-cta-wrap">
          <button className="lp-cta-btn" onClick={onGetStarted}>
            Get started free <span aria-hidden="true">→</span>
          </button>
          <p className="lp-cta-note">3 free listings · No credit card required</p>
        </div>
      </section>

      {/* ── How it works */}
      <section className="lp-steps">
        <h2 className="lp-section-title">How it works</h2>
        <div className="lp-steps-list">
          <div className="lp-step">
            <div className="lp-step-aside">
              <div className="lp-step-num">1</div>
              <div className="lp-step-connector" />
            </div>
            <div className="lp-step-body">
              <span className="lp-step-icon">📸</span>
              <h3 className="lp-step-title">Upload your photos</h3>
              <p className="lp-step-desc">Take a few photos of the item and upload straight from your phone.</p>
            </div>
          </div>

          <div className="lp-step">
            <div className="lp-step-aside">
              <div className="lp-step-num">2</div>
              <div className="lp-step-connector" />
            </div>
            <div className="lp-step-body">
              <span className="lp-step-icon">✨</span>
              <h3 className="lp-step-title">AI writes your listing</h3>
              <p className="lp-step-desc">Formatted titles, descriptions and dimensions — ready to copy straight to Facebook Marketplace.</p>
            </div>
          </div>

          <div className="lp-step lp-step--last">
            <div className="lp-step-aside">
              <div className="lp-step-num">3</div>
            </div>
            <div className="lp-step-body">
              <span className="lp-step-icon">🛒</span>
              <h3 className="lp-step-title">Post and sell</h3>
              <p className="lp-step-desc">One tap to copy your listing to Facebook Marketplace, formatted and ready to go.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Pricing */}
      <section className="lp-pricing">
        <div className="lp-pricing-inner">
          <h2 className="lp-pricing-title">Simple, transparent pricing</h2>
          <p className="lp-pricing-sub">Start with 3 free listings — no credit card needed.</p>

          {/* Unified card: features once + selectable plan chips */}
          <div className="lp-unified-card">
            {/* Feature list */}
            <ul className="lp-features" aria-label="What's included">
              {FEATURES.map((f) => (
                <li key={f.text} className="lp-feature">
                  <span className="lp-feature-icon" aria-hidden="true">{f.icon}</span>
                  <span className="lp-feature-text">{f.text}</span>
                </li>
              ))}
            </ul>

            {/* Thick divider */}
            <div className="lp-card-divider" aria-hidden="true" />

            {/* Plan chip cards */}
            <div className="lp-chips" role="radiogroup" aria-label="Choose a billing period">
              {PLANS.map((plan) => {
                const isOn = plan.key === selectedPlan;
                return (
                  <button
                    key={plan.key}
                    role="radio"
                    aria-checked={isOn}
                    className={`lp-chip${isOn ? ' lp-chip--on' : ''}`}
                    onClick={() => setSelectedPlan(plan.key)}
                  >
                    {plan.badge ? (
                      <span className={`lp-chip-badge lp-chip-badge--${plan.badgeKind}`}>
                        {plan.badge}
                      </span>
                    ) : (
                      <span className="lp-chip-badge-ph" aria-hidden="true" />
                    )}
                    <span className="lp-chip-label">{plan.label}</span>
                    <span className="lp-chip-price">{plan.price}</span>
                    <span className="lp-chip-period">{plan.period}</span>
                    {plan.perMonth ? (
                      <span className="lp-chip-permonth">{plan.perMonth}</span>
                    ) : (
                      <span className="lp-chip-permonth lp-chip-permonth--empty" aria-hidden="true" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Single CTA */}
          <button className="lp-cta-btn lp-pricing-cta" onClick={onGetStarted}>
            Start 3 free listings <span aria-hidden="true">→</span>
          </button>

          <p className="lp-pricing-note">
            Unlimited listings after your free trial. Cancel anytime.
          </p>
        </div>
      </section>

      {/* ── Bottom CTA */}
      <section className="lp-bottom-cta">
        <h2 className="lp-bottom-title">Ready to sell smarter?</h2>
        <p className="lp-bottom-sub">Start with 3 free listings today.</p>
        <div className="lp-bottom-btn-wrap">
          <button className="lp-cta-btn lp-cta-btn--light" onClick={onGetStarted}>
            Create your free account <span aria-hidden="true">→</span>
          </button>
        </div>
      </section>

      {/* ── Footer */}
      <footer className="lp-footer">
        <p>
          © {new Date().getFullYear()} sold4you
          {' · '}
          <a href="/privacy" className="lp-footer-link">Privacy Policy</a>
          {' · '}
          <a href="mailto:support@sold4you.app" className="lp-footer-link">support@sold4you.app</a>
        </p>
      </footer>

      <style>{`
        .lp-shell, .lp-shell * { box-sizing: border-box; }

        .lp-shell {
          min-height: 100vh; width: 100%;
          background: #F5F7F4;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          color: #111827; overflow-x: hidden;
        }

        .lp-safe-top { height: env(safe-area-inset-top, 0px); background: rgba(245,247,244,0.94); }

        /* ── Header */
        .lp-header {
          position: sticky; top: 0; z-index: 10;
          background: rgba(245,247,244,0.94);
          backdrop-filter: blur(12px); -webkit-backdrop-filter: blur(12px);
          border-bottom: 0.5px solid #dde3d9;
        }
        .lp-header-inner {
          max-width: 640px; margin: 0 auto;
          padding: 13px 20px;
          display: flex; align-items: center; justify-content: space-between;
        }
        .lp-logo { display: flex; align-items: center; gap: 8px; }
        .lp-logo-mark { width: 28px; height: 28px; border-radius: 7px; object-fit: cover; flex-shrink: 0; }
        .lp-logo-text { font-size: 17px; font-weight: 600; color: #111827; letter-spacing: -0.3px; }
        .lp-signin-btn {
          flex-shrink: 0; background: none; border: 0.5px solid #bbc9b5;
          border-radius: 20px; padding: 7px 18px;
          font-family: inherit; font-size: 13px; font-weight: 500;
          color: #4A7C59; cursor: pointer;
          -webkit-tap-highlight-color: transparent; transition: background 0.15s;
        }
        .lp-signin-btn:active { background: #EAF3DE; }

        /* ── Hero */
        .lp-hero {
          max-width: 480px; margin: 0 auto;
          padding: 44px 28px 40px;
          display: flex; flex-direction: column; align-items: center; text-align: center;
        }
        .lp-mascot-wrap {
          width: 100px; height: 100px; border-radius: 24px; overflow: hidden;
          margin-bottom: 28px;
          box-shadow: 0 8px 28px rgba(74,124,89,0.18), 0 2px 8px rgba(0,0,0,0.07);
          flex-shrink: 0;
        }
        .lp-mascot { width: 100%; height: 100%; object-fit: cover; display: block; }
        .lp-headline { font-size: 30px; font-weight: 700; line-height: 1.18; color: #111827; margin: 0 0 20px; letter-spacing: -0.6px; }
        .lp-sub { font-size: 15px; line-height: 1.65; color: #5C6B74; margin: 0 0 4px; width: 100%; }
        .lp-sub--lead { color: #374151; font-weight: 500; margin-bottom: 8px; }
        .lp-sub--accent { font-weight: 600; color: #4A7C59; margin-bottom: 0; }
        .lp-cta-wrap { width: 100%; margin-top: 32px; }
        .lp-cta-btn {
          display: flex; align-items: center; justify-content: center; gap: 8px;
          background: #4A7C59; color: #EAF3DE; border: none; border-radius: 14px;
          padding: 0 24px; height: 52px;
          font-family: inherit; font-size: 16px; font-weight: 600;
          cursor: pointer; width: 100%;
          transition: background 0.15s, transform 0.1s;
          -webkit-tap-highlight-color: transparent; letter-spacing: -0.1px;
        }
        .lp-cta-btn:active { transform: scale(0.98); background: #3d6b4a; }
        .lp-cta-btn--light { background: #EAF3DE; color: #2d5c3d; }
        .lp-cta-btn--light:active { background: #d6eccc; transform: scale(0.98); }
        .lp-cta-note { margin-top: 11px; font-size: 12px; color: #9CA3AF; letter-spacing: 0.1px; }

        /* ── Steps */
        .lp-steps { background: #fff; border-top: 0.5px solid #dde3d9; border-bottom: 0.5px solid #dde3d9; padding: 40px 0; }
        .lp-section-title { font-size: 17px; font-weight: 600; letter-spacing: -0.2px; text-align: center; margin: 0 0 28px; color: #111827; }
        .lp-steps-list { max-width: 480px; margin: 0 auto; padding: 0 28px; display: flex; flex-direction: column; }
        .lp-step { display: flex; gap: 16px; align-items: flex-start; }
        .lp-step-aside { display: flex; flex-direction: column; align-items: center; flex-shrink: 0; padding-top: 1px; }
        .lp-step-num {
          width: 26px; height: 26px; background: #4A7C59; color: #EAF3DE;
          border-radius: 50%; font-size: 12px; font-weight: 700;
          display: flex; align-items: center; justify-content: center; flex-shrink: 0;
        }
        .lp-step-connector { width: 1.5px; flex: 1; min-height: 20px; background: #dde3d9; margin: 6px 0; }
        .lp-step-body { padding-bottom: 28px; flex: 1; }
        .lp-step--last .lp-step-body { padding-bottom: 0; }
        .lp-step-icon { font-size: 20px; line-height: 1; display: block; margin-bottom: 5px; }
        .lp-step-title { font-size: 14px; font-weight: 600; color: #111827; margin: 0 0 3px; }
        .lp-step-desc { font-size: 13px; color: #6B7280; line-height: 1.65; margin: 0; }

        /* ── Pricing */
        .lp-pricing { padding: 48px 0 52px; background: #F5F7F4; border-bottom: 0.5px solid #dde3d9; }
        .lp-pricing-inner { max-width: 480px; margin: 0 auto; padding: 0 20px; }
        .lp-pricing-title { font-size: 17px; font-weight: 600; letter-spacing: -0.2px; text-align: center; margin: 0 0 8px; color: #111827; }
        .lp-pricing-sub { font-size: 14px; color: #6B7280; text-align: center; margin: 0 0 24px; }

        /* Unified card */
        .lp-unified-card {
          background: #fff;
          border: 1px solid #dde3d9;
          border-radius: 18px;
          overflow: hidden;
          margin-bottom: 16px;
        }

        /* Feature list */
        .lp-features { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; }
        .lp-feature {
          display: flex; align-items: center; gap: 12px;
          padding: 11px 18px;
          border-bottom: 0.5px solid #dde3d9;
        }
        .lp-feature:last-child { border-bottom: none; }
        .lp-feature-icon { font-size: 16px; flex-shrink: 0; width: 22px; text-align: center; }
        .lp-feature-text { font-size: 13px; color: #374151; }

        /* Card divider */
        .lp-card-divider {
          height: 8px;
          background: #F5F7F4;
          border-top: 0.5px solid #dde3d9;
          border-bottom: 0.5px solid #dde3d9;
        }

        /* Plan chip cards row */
        .lp-chips {
          display: grid; grid-template-columns: repeat(3, 1fr);
          gap: 8px; padding: 14px 14px 16px;
          background: #fff;
        }

        .lp-chip {
          position: relative;
          display: flex; flex-direction: column; align-items: center;
          padding: 10px 6px;
          background: #F5F7F4;
          border: 1.5px solid #dde3d9;
          border-radius: 12px;
          font-family: inherit; cursor: pointer;
          transition: border-color 0.14s, background 0.14s;
          -webkit-tap-highlight-color: transparent;
          gap: 2px;
        }
        .lp-chip:active { opacity: 0.85; }
        .lp-chip--on {
          border-color: #4A7C59;
          background: #EAF3DE;
        }

        .lp-chip-badge {
          font-size: 9px; font-weight: 700; letter-spacing: 0.2px;
          padding: 2px 6px; border-radius: 9999px; white-space: nowrap;
          margin-bottom: 4px; line-height: 1.3;
        }
        .lp-chip-badge--primary { background: #4A7C59; color: #EAF3DE; }
        .lp-chip-badge--subtle {
          background: #EAF3DE; color: #2d5c3d;
          border: 0.5px solid #4A7C59;
        }
        .lp-chip-badge-ph { display: block; height: 18px; margin-bottom: 4px; }

        .lp-chip-label {
          font-size: 10px; font-weight: 600;
          color: #6B7280; text-transform: uppercase; letter-spacing: 0.5px;
          margin-bottom: 2px;
        }
        .lp-chip--on .lp-chip-label { color: #4A7C59; }

        .lp-chip-price { font-size: 15px; font-weight: 700; color: #111827; letter-spacing: -0.4px; line-height: 1.1; }
        .lp-chip-period { font-size: 10px; color: #9CA3AF; line-height: 1.2; }

        .lp-chip-permonth {
          font-size: 10px; font-weight: 500; color: #4A7C59;
          margin-top: 3px; line-height: 1.2;
        }
        .lp-chip-permonth--empty { display: block; height: 14px; margin-top: 3px; }

        /* Single CTA below card */
        .lp-pricing-cta { margin-bottom: 0; }

        .lp-pricing-note { font-size: 12px; color: #9CA3AF; text-align: center; margin-top: 16px; line-height: 1.6; }

        /* ── Bottom CTA */
        .lp-bottom-cta { background: #4A7C59; padding: 48px 28px; text-align: center; }
        .lp-bottom-title { font-size: 21px; font-weight: 700; color: #EAF3DE; letter-spacing: -0.4px; margin: 0 0 8px; }
        .lp-bottom-sub { font-size: 14px; color: rgba(234,243,222,0.7); margin: 0 0 24px; }
        .lp-bottom-btn-wrap { max-width: 340px; margin: 0 auto; }

        /* ── Footer */
        .lp-footer { padding: 20px 24px calc(20px + env(safe-area-inset-bottom, 0px)); text-align: center; }
        .lp-footer p { font-size: 12px; color: #9CA3AF; line-height: 1.9; margin: 0; }
        .lp-footer-link { color: #9CA3AF; text-decoration: underline; text-underline-offset: 2px; }
        .lp-footer-link:active { color: #4A7C59; }
      `}</style>
    </div>
  );
}
